import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

interface IFailedQueue {
  onSuccess: (token: string) => void;
  onFailure: (error: AxiosError) => void;
}

interface IRefreshTokenResponse {
  token: string;
  refresh_token: string;
}

interface ICustomAxiosRequestConfig extends AxiosRequestConfig {
  retryRequest?: boolean;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const refreshApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

let isRefreshing = false;
let failedQueue: IFailedQueue[] = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null,
) => {
  failedQueue.forEach(request => {
    if (error) {
      request.onFailure(error);
    } else if (token) {
      request.onSuccess(token);
    }
  });

  failedQueue = [];
};

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalConfig = error.config as ICustomAxiosRequestConfig;

    if (error.response?.status !== 401 || originalConfig?.retryRequest) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('@AdopetCP:refresh_token');

    if (!refreshToken) {
      localStorage.removeItem('@AdopetCP:token');
      localStorage.removeItem('@AdopetCP:refresh_token');
      localStorage.removeItem('@AdopetCP:user');

      delete api.defaults.headers.common.Authorization;

      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          onSuccess: token => {
            if (!originalConfig.headers) {
              originalConfig.headers = {};
            }

            originalConfig.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalConfig));
          },
          onFailure: err => {
            reject(err);
          },
        });
      });
    }

    originalConfig.retryRequest = true;
    isRefreshing = true;

    try {
      const response = await refreshApi.post<IRefreshTokenResponse>(
        '/refresh-token',
        {
          token: refreshToken,
        },
      );

      const { token, refresh_token } = response.data;

      localStorage.setItem('@AdopetCP:token', token);
      localStorage.setItem('@AdopetCP:refresh_token', refresh_token);

      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      processQueue(null, token);

      if (!originalConfig.headers) {
        originalConfig.headers = {};
      }

      originalConfig.headers.Authorization = `Bearer ${token}`;

      return api(originalConfig);
    } catch (refreshError) {
      const axiosRefreshError = refreshError as AxiosError;

      processQueue(axiosRefreshError, null);

      localStorage.removeItem('@AdopetCP:token');
      localStorage.removeItem('@AdopetCP:refresh_token');
      localStorage.removeItem('@AdopetCP:user');

      delete api.defaults.headers.common.Authorization;

      window.location.href = '/login';

      return Promise.reject(axiosRefreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
